import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

// Import the services via the index file in that folder
import './services';

// Imports our global styles/ This must be imported first before any components or
// other styles to ensure it comes first in the compiled css chunk
import './styles/custom.scss';
import './index.scss';

import { setUrls } from './utilities/dataHelpers';
import { _config, getConfig } from './utilities/envConfig';
import * as log from './utilities/log';
import mediator from './utilities/mediator';
import { store } from './reducers/index';

import { getObject, getSessionObject } from './utilities/storage';

import './utilities/testHelper';

import App from './App';
import UnexpectedError from './components/utility/UnexpectedError';
import { ConfigState, User } from './models';
import {
  config as ParallaxConfig,
  LoadingSpinner,
  ParallaxProvider,
  VStack,
} from '@adc/parallax-component-library';

if (process.env.NODE_ENV !== 'production') {
  import('./dev');
}

let appMounted = false;

const container = document.getElementById('app') as HTMLElement;
const root = createRoot(container);

// Finally render our React trees
const renderApp = () => {
  try {
    root.render(
      <Provider store={store}>
        <App />
      </Provider>,
    );
  } catch (e) {
    renderError(e);
    log.error(e);
  }
};

// Let our services know that the app is now ready to start
const startApp = () => {
  mediator.publish('store:created');
};

// Attempt to load a token from the url query on first try, otherwise start the app
const noCurrentSession = (firstTry: boolean) => {
  log.debug('no current session');
  log.debug(firstTry);

  const found = getObject('chosenCountryLang');

  if (found && found.country) {
    mediator
      .request('config:updateConfig', { country: found.country })
      .then(() => {
        startApp();
      });
  } else {
    startApp();
  }
};

// Attempt to get the user data based on session storage and fetch their config before starting the app
const trySession = (firstTry = true) => {
  const user = getSessionObject('user');

  if (user && user.loggedIn) {
    mediator
      .request<User>('user:getUser')
      .then((data) => {
        mediator
          .request('config:updateConfig', { country: data.country || 'US' })
          .then(() => {
            startApp();
          })
          .catch(renderError);
      })
      .catch(() => {
        noCurrentSession(false);
      });
  } else {
    noCurrentSession(firstTry);
  }
};

// We load the full config from server and then try to load a user session
const initialize = (config: typeof _config) => {
  setUrls(config);

  const found = getObject('chosenCountryLang');

  mediator
    .request<ConfigState>('config:updateConfig', {
      country: (found && found.country) || 'US',
    })
    .then((res) => {
      // setUrls(res);
      console.log(res);
      trySession();
    })
    .catch((err) => {
      log.error('failed to get server config: ' + err);
      renderError({ message: 'configNotFound' });
    });
};

// This is the starting point of our app launch life-cycle
const init = () => {
  root.render(
    <ParallaxProvider
      config={ParallaxConfig}
      locale="en-US"
      dir="ltr"
      defaultTheme="light"
    >
      <VStack alignItems="center" justifyContent="center" flexGrow={1}>
        <LoadingSpinner testID="main-layout-loading-spinner" />
      </VStack>
    </ParallaxProvider>,
  );

  getConfig()
    .then((config) => {
      log.init(config.logLevel);

      if (config.mock) {
        mediator.once('MOCK:setup:initialized', () => initialize(config));

        import('./services/mock');
      } else {
        initialize(config);
      }
    })
    .catch((err: Error) => {
      log.error('failed to get the environment config: ' + err);
      renderError(new Error('failed to get the environment config: ' + err));
    });
};

const _errorReset = () => (appMounted ? renderApp() : init());

// Renders our UnexpectedError display
const renderError = (err) => {
  window.setTimeout(() => {
    root.render(
      <ParallaxProvider
        config={ParallaxConfig}
        locale="en-US"
        dir="ltr"
        defaultTheme="light"
      >
        <UnexpectedError reset={_errorReset} errorMessage={err.message} />
      </ParallaxProvider>,
    );
  }, 0);
};

// Begin
init();

// Services that need to publish that they are ready
const appReady = {
  i18n: false,
};

// Listens for all services to be complete so that we have everything we need when we mount React
const readySub = mediator.subscribe(
  'app:ready',
  (ready: Partial<typeof appReady>) => {
    Object.assign(appReady, ready);

    const done = Object.values(appReady).every((value) => !!value);

    if (done) {
      mediator.publish('app:mounted');
      mediator.remove('app:ready', readySub.id);
    }
  },
);

mediator.once('app:mounted', () => {
  appMounted = true;
  renderApp();
});
